/**
 * Tool controller: turns pointer/keyboard input into document operations.
 *
 * Implements the interaction model of the reference apps:
 * - draw with any pointer; stylus eraser end auto-switches to the eraser
 * - finger-draw toggle (Endless Paper): stylus draws, one finger pans,
 *   two fingers pinch-zoom/rotate; in finger mode one finger draws
 * - pinch to zoom + two-finger rotate (trackpad pinch arrives as ctrl+wheel)
 * - eraser zoom modes: erase content above / at / below the current zoom
 * - rectangle & lasso selection with move / resize / duplicate
 * - shapes (line, rect, ellipse), text, image, eyedropper, bookmarks
 * - fill (paint bucket): click a shape to fill it, or click inside a closed
 *   stroke loop to create a solid fill polygon
 * - unlimited undo/redo, clipboard copy/paste between depths and documents
 */

import { brushFor, toStrokeStyle } from "./brushes";
import { Camera } from "./camera";
import { MAX_DEPTH, QUAD_OFFSETS, WORLD_EXTENT } from "./cell";
import { History } from "./history";
import { Mat2D, apply, clamp, dist, vLerp, Vec2 } from "./math";
import {
  Element,
  EraserMode,
  FillElement,
  ImageElement,
  ShapeElement,
  Stroke,
  StrokePoint,
  ToolKind,
} from "./model";
import {
  clipStrokeWithCircle,
  distanceToStroke,
  pressureOf,
  smoothPoints,
  strokeBounds,
  strokeWidthAt,
} from "./strokes";
import { World } from "./world";

export interface ActiveStyleState {
  color: string;
  size: number; // screen px
  opacity: number;
  eraserMode: EraserMode;
  fingerDraw: boolean;
}

export interface SelectionItem {
  el: Element;
}

export type SelectionState =
  | { mode: "none" }
  | {
      mode: "selected";
      items: SelectionItem[];
      box: { x: number; y: number; w: number; h: number };
    };

export interface ToolEvents {
  style: ActiveStyleState;
  tool: ToolKind;
  selection: SelectionState;
  history: void;
  camera: void;
  overlay: void;
  "request-text": { cellId: string; x: number; y: number; targetScale: number };
  "request-image": { cellId: string; x: number; y: number; targetScale: number };
  bookmark: { cellId: string; x: number; y: number; scale: number };
}

type Listener<K extends keyof ToolEvents> = (e: ToolEvents[K]) => void;

export interface PointerState {
  id: number;
  x: number;
  y: number;
  pointerType: string;
  button: number;
  pressure: number;
  shiftKey?: boolean;
}

interface DrawState {
  pointerId: number;
  cellId: string;
  points: StrokePoint[];
  style: Stroke["style"];
  lastScreen: Vec2;
  brushScale: number;
}

interface PinchState {
  startMid: Vec2;
  startDist: number;
  startAngle: number;
  moved: boolean;
}

interface SelectionDrag {
  mode: "move" | "scale";
  start: Vec2;
  startEls: Map<string, Element>;
  duplicate: boolean;
  handle: "nw" | "ne" | "sw" | "se" | null;
  anchor: Vec2; // screen space, fixed corner during scale
}

const SNAP_DIST_PX = 0.25;
const HANDLE_R = 8;

export class ToolController {
  tool: ToolKind = "pen";
  style: ActiveStyleState = {
    color: "#1a1a1a",
    size: 3.5,
    opacity: 1,
    eraserMode: "current",
    fingerDraw: true,
  };

  private world: World;
  private camera: Camera;
  private history: History;
  private listeners = new Map<string, Set<(e: unknown) => void>>();

  private pointers = new Map<number, PointerState>();
  private draw: DrawState | null = null;
  private pinch: PinchState | null = null;
  private panPointer: number | null = null;
  private spaceHeld = false;
  private shiftHeld = false;

  get isPanning(): boolean {
    return this.panPointer !== null;
  }

  get isPanModifierHeld(): boolean {
    return this.spaceHeld || this.shiftHeld;
  }

  resetModifiers(): void {
    this.spaceHeld = false;
    this.shiftHeld = false;
  }

  private marquee: { start: Vec2; current: Vec2; lasso: Vec2[] | null } | null = null;
  selection: SelectionState = { mode: "none" };
  private selDrag: SelectionDrag | null = null;

  private shapeDraft: { kind: "line" | "rect" | "ellipse"; start: Vec2; current: Vec2 } | null = null;

  private clipboard: { els: Element[]; center: Vec2 } | null = null;
  private prevTool: ToolKind = "pen";
  private eraseSession: {
    pointerId: number;
    lastScreen: Vec2;
    originalElements: Map<string, Element>;
    activeReplacements: Map<string, Element>;
  } | null = null;
  private lastPointerPos: Vec2 | null = null;
  private smoothedCursorPos: Vec2 | null = null;
  private textClickStart: Vec2 | null = null;
  private fillClickStart: Vec2 | null = null;

  /** Set by the engine on resize. */
  canvasSize: { w: number; h: number } = { w: 1, h: 1 };

  constructor(world: World, camera: Camera, history: History) {
    this.world = world;
    this.camera = camera;
    this.history = history;
  }

  /* ------------------------------ events --------------------------------- */

  on<K extends keyof ToolEvents>(k: K, fn: Listener<K>): () => void {
    let set = this.listeners.get(k);
    if (!set) this.listeners.set(k, (set = new Set()));
    set.add(fn as (e: unknown) => void);
    return () => set!.delete(fn as (e: unknown) => void);
  }

  private emit<K extends keyof ToolEvents>(k: K, e: ToolEvents[K]): void {
    this.listeners.get(k)?.forEach((fn) => fn(e));
  }

  /* ------------------------------ settings ------------------------------- */

  setTool(tool: ToolKind): void {
    this.tool = tool;
    if (tool !== "eyedropper") this.prevTool = tool;
    this.cancelTransient();
    this.emit("tool", tool);
  }

  setColor(color: string): void {
    this.style.color = color;
    this.emit("style", this.style);
  }

  setSize(size: number): void {
    this.style.size = size;
    this.emit("style", this.style);
  }

  setOpacity(opacity: number): void {
    this.style.opacity = opacity;
    this.emit("style", this.style);
  }

  setEraserMode(mode: EraserMode): void {
    this.style.eraserMode = mode;
    this.emit("style", this.style);
  }

  setFingerDraw(on: boolean): void {
    this.style.fingerDraw = on;
    this.emit("style", this.style);
  }

  private cancelTransient(): void {
    if (this.eraseSession) {
      this.finishErase();
    }
    this.draw = null;
    this.world.setActiveStroke(null);
    this.pinch = null;
    this.marquee = null;
    this.selDrag = null;
    this.shapeDraft = null;
    this.textClickStart = null;
    this.fillClickStart = null;
    if (this.selection.mode !== "none") this.clearSelection();
  }

  /* --------------------------- pointer handling -------------------------- */

  down(p: PointerState, ctrl: boolean): void {
    this.pointers.set(p.id, p);
    this.lastPointerPos = { x: p.x, y: p.y };
    if (this.pointers.size >= 2) {
      if (this.eraseSession) {
        this.finishErase();
      }
      if (this.draw) {
        this.world.setActiveStroke(null);
        this.draw = null;
      }
      this.panPointer = null;
      this.marquee = null;
      this.selDrag = null;
      this.shapeDraft = null;
      if (this.pointers.size === 2) {
        this.beginPinch();
      }
      return;
    }

    const isPan =
      p.button === 1 ||
      p.button === 2 ||
      this.spaceHeld ||
      this.shiftHeld ||
      Boolean(p.shiftKey) ||
      (p.pointerType === "touch" && !this.style.fingerDraw);

    if (isPan) {
      this.panPointer = p.id;
      return;
    }

    const eraserPointer = p.pointerType === "eraser";
    const effectiveTool: ToolKind =
      eraserPointer ? "eraser" : this.tool;

    switch (effectiveTool) {
      case "pencil":
      case "pen":
      case "marker":
      case "highlighter":
        this.beginStroke(p, effectiveTool);
        break;
      case "eraser":
        this.beginErase(p);
        break;
      case "select":
        if (
          this.selection.mode === "selected" &&
          (hitHandle(p, this.selection.box) || pointInBox(p, this.selection.box))
        ) {
          this.beginSelectionDrag(p, ctrl);
        } else {
          this.clearSelection();
          this.marquee = { start: { x: p.x, y: p.y }, current: { x: p.x, y: p.y }, lasso: null };
          this.emit("overlay", undefined);
        }
        break;
      case "lasso":
        this.marquee = { start: { x: p.x, y: p.y }, current: { x: p.x, y: p.y }, lasso: [{ x: p.x, y: p.y }] };
        this.emit("overlay", undefined);
        break;
      case "line":
      case "rect":
      case "ellipse":
        this.shapeDraft = { kind: effectiveTool, start: { x: p.x, y: p.y }, current: { x: p.x, y: p.y } };
        this.emit("overlay", undefined);
        break;
      case "text":
        this.textClickStart = { x: p.x, y: p.y };
        break;
      case "fill":
        this.fillClickStart = { x: p.x, y: p.y };
        break;
      case "image":
        this.requestImage(p);
        break;
      case "eyedropper":
        this.eyedrop(p);
        break;
      case "bookmark":
        this.placeBookmark(p);
        break;
    }
  }

  move(p: PointerState): void {
    const prev = this.pointers.get(p.id);
    this.pointers.set(p.id, p);
    this.lastPointerPos = { x: p.x, y: p.y };
    if (!prev) return;

    if (this.pinch && this.pointers.size >= 2) {
      this.updatePinch();
      return;
    }

    if (this.panPointer === p.id) {
      this.camera.panBy(p.x - prev.x, p.y - prev.y);
      this.emit("camera", undefined);
      return;
    }

    if (this.eraseSession) {
      if (this.eraseSession.pointerId === p.id) {
        this.extendErase(p);
      }
      return;
    }

    if (this.draw) {
      if (this.draw.pointerId === p.id) {
        this.extendStroke(p);
      }
      return;
    }

    if (this.marquee) {
      this.marquee.current = { x: p.x, y: p.y };
      if (this.marquee.lasso && dist(p, this.marquee.lasso[this.marquee.lasso.length - 1]!) > 2) {
        this.marquee.lasso.push({ x: p.x, y: p.y });
      }
      this.emit("overlay", undefined);
      return;
    }

    if (this.selDrag) {
      this.updateSelDrag(p);
      return;
    }

    if (this.shapeDraft) {
      this.shapeDraft.current = { x: p.x, y: p.y };
      this.emit("overlay", undefined);
      return;
    }
  }

  up(p: PointerState): void {
    this.pointers.delete(p.id);
    this.lastPointerPos = { x: p.x, y: p.y };

    if (this.pinch) {
      if (this.pointers.size < 2) {
        this.pinch = null;
        const rest = [...this.pointers.values()][0];
        if (rest) this.panPointer = rest.id;
      }
      return;
    }

    if (this.panPointer === p.id) {
      this.panPointer = null;
      return;
    }

    if (this.eraseSession) {
      if (this.eraseSession.pointerId === p.id) {
        this.finishErase();
      }
      return;
    }

    if (this.draw) {
      if (this.draw.pointerId === p.id) {
        this.finishStroke();
      }
      return;
    }

    if (this.marquee) {
      this.finishMarquee();
      return;
    }

    if (this.selDrag) {
      this.commitSelDrag();
      return;
    }

    if (this.shapeDraft) {
      this.finishShape();
      return;
    }

    if (this.textClickStart) {
      const start = this.textClickStart;
      this.textClickStart = null;
      if (dist(p, start) < 5) {
        this.requestText(p);
      }
      return;
    }

    if (this.fillClickStart) {
      const start = this.fillClickStart;
      this.fillClickStart = null;
      if (dist(p, start) < 6) {
        this.fillAt({ x: p.x, y: p.y });
      }
      return;
    }
  }

  cancel(p: PointerState): void {
    this.pointers.delete(p.id);
    this.textClickStart = null;
    this.fillClickStart = null;
    if (this.panPointer === p.id) {
      this.panPointer = null;
    }
    if (this.eraseSession && this.eraseSession.pointerId === p.id) {
      this.finishErase();
    }
    if (this.draw && this.draw.pointerId === p.id) {
      this.world.setActiveStroke(null);
      this.draw = null;
    }
    if (this.shapeDraft) {
      this.shapeDraft = null;
      this.emit("overlay", undefined);
    }
    if (this.marquee) {
      this.marquee = null;
      this.emit("overlay", undefined);
    }
    this.pinch = null;
  }

  /* ------------------------------ pinch ---------------------------------- */

  private beginPinch(): void {
    const pts = [...this.pointers.values()];
    const a = pts[0]!;
    const b = pts[1]!;
    this.pinch = {
      startMid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
      startDist: Math.max(1, dist(a, b)),
      startAngle: Math.atan2(b.y - a.y, b.x - a.x),
      moved: false,
    };
  }

  private updatePinch(): void {
    if (!this.pinch) return;
    const pts = [...this.pointers.values()];
    if (pts.length < 2) return;
    const a = pts[0]!;
    const b = pts[1]!;
    const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    const d = Math.max(1, dist(a, b));
    const angle = Math.atan2(b.y - a.y, b.x - a.x);
    const cx = this.canvasSize.w / 2;
    const cy = this.canvasSize.h / 2;

    this.camera.panBy(mid.x - this.pinch.startMid.x, mid.y - this.pinch.startMid.y);
    this.camera.zoomAt(cx, cy, mid, d / this.pinch.startDist);
    this.camera.rotateAt(cx, cy, mid, angle - this.pinch.startAngle);

    this.pinch.startMid = mid;
    this.pinch.startDist = d;
    this.pinch.startAngle = angle;
    this.pinch.moved = true;
    this.emit("camera", undefined);
  }

  /* ------------------------------- wheel --------------------------------- */

  wheel(e: WheelEvent): void {
    const pos = { x: e.offsetX ?? this.canvasSize.w / 2, y: e.offsetY ?? this.canvasSize.h / 2 };
    const cx = this.canvasSize.w / 2;
    const cy = this.canvasSize.h / 2;
    if (e.deltaY !== 0) {
      let dy = e.deltaY;
      if (e.deltaMode === 1) dy *= 24;
      else if (e.deltaMode === 2) dy *= 100;
      const factor = Math.exp(-dy * 0.0022);
      this.camera.zoomAt(cx, cy, pos, clamp(factor, 0.2, 5));
    } else if (e.deltaX !== 0) {
      this.camera.panBy(-e.deltaX, 0);
    }
    this.emit("camera", undefined);
    e.preventDefault();
  }

  /* ------------------------------ keyboard ------------------------------- */

  keydown(e: KeyboardEvent): boolean {
    const mod = e.ctrlKey || e.metaKey;
    const k = e.key.toLowerCase();

    if (e.code === "Space") {
      this.spaceHeld = true;
      return true;
    }
    if (e.key === "Shift") {
      this.shiftHeld = true;
    }
    if (mod && k === "z") {
      if (e.shiftKey) this.history.redo();
      else this.history.undo();
      return true;
    }
    if (mod && k === "y") {
      this.history.redo();
      return true;
    }
    if (mod && k === "c" && this.selection.mode === "selected") {
      this.copySelection();
      return true;
    }
    if (mod && k === "v") {
      this.paste();
      return true;
    }
    if (mod && k === "a") {
      this.selectAll();
      return true;
    }
    if (k === "escape") {
      this.cancelTransient();
      return true;
    }
    if ((k === "delete" || k === "backspace") && this.selection.mode === "selected") {
      this.deleteSelection();
      return true;
    }
    if (mod) return false;

    switch (k) {
      case "p": this.setTool("pen"); return true;
      case "g": this.setTool("pencil"); return true;
      case "m": this.setTool("marker"); return true;
      case "h": this.setTool("highlighter"); return true;
      case "e": this.setTool("eraser"); return true;
      case "f": this.setTool("fill"); return true;
      case "v": this.setTool("select"); return true;
      case "q": this.setTool("lasso"); return true;
      case "t": this.setTool("text"); return true;
      case "l": this.setTool("line"); return true;
      case "o": this.setTool("ellipse"); return true;
      case "i": this.setTool("eyedropper"); return true;
      case "b": this.setTool("bookmark"); return true;
      case "=":
      case "+": {
        this.camera.zoomAt(this.canvasSize.w / 2, this.canvasSize.h / 2, { x: this.canvasSize.w / 2, y: this.canvasSize.h / 2 }, 1.3);
        this.emit("camera", undefined);
        return true;
      }
      case "-": {
        this.camera.zoomAt(this.canvasSize.w / 2, this.canvasSize.h / 2, { x: this.canvasSize.w / 2, y: this.canvasSize.h / 2 }, 1 / 1.3);
        this.emit("camera", undefined);
        return true;
      }
    }
    return false;
  }

  keyup(e: KeyboardEvent): boolean {
    if (e.code === "Space") {
      this.spaceHeld = false;
      return true;
    }
    if (e.key === "Shift") {
      this.shiftHeld = false;
      return true;
    }
    return false;
  }

  /* ------------------------------ drawing -------------------------------- */

  /** Screen px per cell-unit at a given cell depth (deeper = smaller units). */
  private pxPerUnit(depth: number): number {
    return this.camera.scale * Math.pow(2, this.camera.depth - depth);
  }

  /**
   * Choose the cell a new stroke should live in.
   *
   * Descend from the camera's focus cell only while BOTH hold:
   *  - the pointer is within the child cell's inner region, and
   *  - the brush is still thinner than the minimum healthy width.
   * This keeps stroke coordinates bounded near [-1, 1] in whatever cell the
   * stroke ends up in — never hundreds of cell-units out of bounds.
   */
  private strokeCellAt(
    screenPt: Vec2,
    sizePx: number,
    mode: EraserMode,
  ): { cellId: string; point: Vec2; brushScale: number } {
    const { camera } = this;
    const MIN_WIDTH = 0.02; // cell units; ~3.4px in a tile at default cache res
    const INNER = 0.75; // stay well inside the child quadrant while descending

    const focusPt = camera.screenToFocus(this.canvasSize.w / 2, this.canvasSize.h / 2, screenPt);
    let cellId = camera.focusCellId;
    let point = focusPt;

    while (cellId.length < MAX_DEPTH) {
      const widthHere = sizePx / this.pxPerUnit(cellId.length);
      if (widthHere >= MIN_WIDTH) break; // crisp enough at this depth
      if (Math.abs(point.x) > INNER || Math.abs(point.y) > INNER) break; // pointer not inside a child
      const qx = point.x >= 0 ? 1 : 0;
      const qy = point.y >= 0 ? 1 : 0;
      const q = (qy * 2 + qx) as 0 | 1 | 2 | 3;
      cellId = cellId + String(q);
      const [ox, oy] = QUAD_OFFSETS[q]!;
      point = { x: (point.x - ox) * 2, y: (point.y - oy) * 2 };
    }

    // Eraser zoom modes shift one level relative to the chosen cell.
    if (mode === "above" && cellId.length > 0) {
      const cell = this.world.tree.get(cellId);
      point = apply(cell.localToParent, point);
      cellId = cell.parentId!;
    }
    if (mode === "below" && cellId.length < MAX_DEPTH) {
      const cell = this.world.tree.get(cellId);
      const q = point.x >= 0 ? (point.y >= 0 ? 3 : 1) : point.y >= 0 ? 2 : 0;
      cellId = cellId + String(q as 0 | 1 | 2 | 3);
      const [ox, oy] = QUAD_OFFSETS[q as 0 | 1 | 2 | 3]!;
      point = { x: (point.x - ox) * 2, y: (point.y - oy) * 2 };
    }

    this.world.tree.ensure(cellId);
    const brushScale = this.pxPerUnit(cellId.length);
    return { cellId, point, brushScale };
  }

  private beginErase(p: PointerState): void {
    this.eraseSession = {
      pointerId: p.id,
      lastScreen: { x: p.x, y: p.y },
      originalElements: new Map(),
      activeReplacements: new Map(),
    };
    this.eraseAt({ x: p.x, y: p.y });
  }

  private extendErase(p: PointerState): void {
    if (!this.eraseSession || this.eraseSession.pointerId !== p.id) return;
    const prev = this.eraseSession.lastScreen;
    const curr = { x: p.x, y: p.y };
    const d = dist(prev, curr);
    const radiusPx = Math.max(this.style.size / 2, 8);
    const step = Math.max(radiusPx / 2, 4);

    if (d >= step) {
      const steps = Math.min(Math.ceil(d / step), 50);
      for (let i = 1; i <= steps; i++) {
        const t = i / steps;
        this.eraseAt({
          x: prev.x + (curr.x - prev.x) * t,
          y: prev.y + (curr.y - prev.y) * t,
        });
      }
      this.eraseSession.lastScreen = curr;
    }
  }

  private eraseAt(screenPt: Vec2): void {
    if (!this.eraseSession) return;
    const { w: cw, h: ch } = this.canvasSize;
    const radiusPx = Math.max(this.style.size / 2, 8);
    const mode = this.style.eraserMode;
    const camDepth = this.camera.depth;

    // Never erase content the user cannot see: a hidden layer must not lose
    // elements to a stroke they can't even aim at. (The eyedropper applies the
    // same rule.)
    const visibleLayers = new Set(this.world.layers.filter((l) => l.visible).map((l) => l.id));

    const allCells = this.world.allCells();
    for (const [cellId, els] of Object.entries(allCells)) {
      if (els.length === 0) continue;
      const cellDepth = cellId.length;

      // Filter by eraserMode
      if (mode === "above" && cellDepth >= camDepth) continue;
      if (mode === "below" && cellDepth <= camDepth) continue;

      const m = this.camera.cellToScreen(cw / 2, ch / 2, cellId);
      const pxPerUnit = linearScaleOf(m);
      if (pxPerUnit <= 0) continue;
      const cellRadius = radiusPx / pxPerUnit;
      const ptInCell = this.camera.screenToCellPoint(cw / 2, ch / 2, cellId, screenPt);

      for (const el of els) {
        if (!visibleLayers.has(el.layerId)) continue;
        if (el.kind === "stroke") {
          const d = distanceToStroke(el, ptInCell);
          const strokeHalfWidth = (el.style.size || 0) / 2;
          const hitRadius = cellRadius + strokeHalfWidth;
          if (d > hitRadius) continue;

          // Hit! Slice this stroke with the circle
          const remainingPieces = clipStrokeWithCircle(el.points, ptInCell, hitRadius);

          // Track for undo/redo
          if (this.eraseSession.activeReplacements.has(el.id)) {
            this.eraseSession.activeReplacements.delete(el.id);
          } else {
            this.eraseSession.originalElements.set(el.id, el);
          }

          // Remove the existing stroke from the world
          this.world.removeElement(el.cellId, el.layerId, el.id);

          // Add back the remaining sliced pieces
          for (const pts of remainingPieces) {
            const pieceStroke: Stroke = {
              kind: "stroke",
              id: newId("st"),
              layerId: el.layerId,
              cellId: el.cellId,
              points: pts,
              style: el.style,
            };
            this.world.addElement(pieceStroke);
            this.eraseSession.activeReplacements.set(pieceStroke.id, pieceStroke);
          }
        } else {
          // Shape, fill, text, image.
          //
          // Only strokes get *partial* erase (they are sliced into surviving
          // polylines, which is exact and lossless). Every other element kind
          // is removed as a unit once the eraser touches it.
          //
          // This is deliberate, not laziness. A partial erase of a shape or
          // fill has to subtract a disc from a filled region, i.e. produce a
          // polygon with a hole, and the element/canvas model here has no
          // representation for that. An earlier attempt clipped the shape
          // outline against the disc and re-emitted the remainder as
          // `fill` elements; it was wrong in both directions — erasing the
          // interior of a shape was a silent no-op (the "all vertices are
          // outside the disc" fast path returned the polygon untouched) and
          // erasing a single corner destroyed the entire shape (the
          // entering-the-disc branch discarded the vertices accumulated so
          // far). It also downgraded every partially erased shape to a solid
          // fill, losing the outline. Whole-element removal is predictable,
          // cannot lose work, and never changes what something looks like.
          // See packages/engine/src/__tests__/eraser-elements.test.ts.
          const b = elementBoundsInCell(el);
          if (
            ptInCell.x < b.minX - cellRadius ||
            ptInCell.x > b.maxX + cellRadius ||
            ptInCell.y < b.minY - cellRadius ||
            ptInCell.y > b.maxY + cellRadius
          ) {
            continue;
          }
          if (el.kind === "fill") {
            // A bbox hit is not enough for a fill: its corners can be far from
            // the actual polygon, so require the disc to touch the polygon
            // itself (inside it, or within the disc of an edge).
            const inside = el.points.length >= 3 && pointInPolygon(ptInCell, el.points);
            if (!inside && distanceToPolygon(el.points, ptInCell) > cellRadius) continue;
          }
          if (this.eraseSession.activeReplacements.has(el.id)) {
            this.eraseSession.activeReplacements.delete(el.id);
          } else {
            this.eraseSession.originalElements.set(el.id, el);
          }
          this.world.removeElement(el.cellId, el.layerId, el.id);
        }
      }
    }
  }

  private finishErase(): void {
    if (!this.eraseSession) return;
    const session = this.eraseSession;
    this.eraseSession = null;
    const removed = Array.from(session.originalElements.values());
    const added = Array.from(session.activeReplacements.values());

    if (removed.length > 0 || added.length > 0) {
      if (added.length === 0) {
        this.history.push({ type: "remove", elements: removed });
      } else if (removed.length === 0) {
        this.history.push({ type: "add", elements: added });
      } else {
        this.history.push({ type: "modify", removed, added });
      }
      this.emit("history", undefined);
    }
  }

  private beginStroke(p: PointerState, tool: ToolKind): void {
    const mode = tool === "eraser" ? this.style.eraserMode : "current";
    const { cellId, point, brushScale } = this.strokeCellAt(
      { x: p.x, y: p.y },
      this.style.size,
      mode,
    );

    const style = toStrokeStyle({
      tool,
      color: this.style.color,
      size: this.style.size / brushScale, // cell-local base size
      opacity: this.style.opacity,
      eraserMode: mode,
    });

    const points: StrokePoint[] = [{ x: point.x, y: point.y, p: pressureOf(p.pressure) }];
    this.draw = {
      pointerId: p.id,
      cellId,
      points,
      style,
      lastScreen: { x: p.x, y: p.y },
      brushScale,
    };
    this.syncActiveStroke();
  }

  /** Keep world.activeStroke in sync with the in-flight draw state. */
  private syncActiveStroke(): void {
    if (!this.draw) return;
    const live: Stroke = {
      kind: "stroke",
      id: "__live__",
      layerId: this.activeLayerId(),
      cellId: this.draw.cellId,
      points: this.draw.points,
      style: this.draw.style,
    };
    this.world.setActiveStroke(live);
  }

  private extendStroke(p: PointerState): void {
    if (!this.draw || this.draw.pointerId !== p.id) return;
    if (dist(p, this.draw.lastScreen) < SNAP_DIST_PX) return;
    this.draw.lastScreen = { x: p.x, y: p.y };

    let point = this.camera.screenToCellPoint(
      this.canvasSize.w / 2,
      this.canvasSize.h / 2,
      this.draw.cellId,
      { x: p.x, y: p.y },
    );

    // If the stroke leaves the cell, promote it to parent cells until it
    // fits (or the root is reached). Each level halves coordinates, so this
    // converges fast and keeps points bounded. At the root (the top-level
    // board) points beyond the edge are clamped — like the edge of paper.
    const limit = WORLD_EXTENT;
    while (Math.abs(point.x) > limit || Math.abs(point.y) > limit) {
      const cell = this.world.tree.get(this.draw.cellId);
      const parent = this.world.tree.parentOf(cell);
      if (!parent) {
        point = {
          x: Math.max(-limit, Math.min(limit, point.x)),
          y: Math.max(-limit, Math.min(limit, point.y)),
        };
        break;
      }
      const m = cell.localToParent;
      this.draw.points = this.draw.points.map((q) => ({
        x: m.a * q.x + m.c * q.y + m.e,
        y: m.b * q.x + m.d * q.y + m.f,
        p: q.p,
      }));
      this.draw.cellId = parent.id;
      this.draw.style = { ...this.draw.style, size: this.draw.style.size * 0.5 };
      this.draw.brushScale *= 2;
      point = this.camera.screenToCellPoint(
        this.canvasSize.w / 2,
        this.canvasSize.h / 2,
        this.draw.cellId,
        { x: p.x, y: p.y },
      );
    }

    this.draw.points.push({ x: point.x, y: point.y, p: pressureOf(p.pressure) });
    this.syncActiveStroke();
  }

  private finishStroke(): void {
    const draw = this.draw;
    this.world.setActiveStroke(null);
    this.draw = null;
    if (!draw || draw.points.length === 0) return;
    // Final fit: if the pointer was released outside the cell, promote the
    // stroke to parent cells until every point is within bounds.
    this.fitDrawToCell(draw);
    const points = smoothPoints(draw.points);
    const stroke: Stroke = {
      kind: "stroke",
      id: newId("st"),
      layerId: this.activeLayerId(),
      cellId: draw.cellId,
      points,
      style: draw.style,
    };
    this.world.addElement(stroke);
    this.history.push({ type: "add", elements: [stroke] });
  }

  /** Promote a draw state to parent cells until all points fit; clamp at the
   *  root edge (the top-level board boundary). */
  private fitDrawToCell(draw: DrawState): void {
    const limit = WORLD_EXTENT;
    const clampPt = (q: StrokePoint): StrokePoint => ({
      x: Math.max(-limit, Math.min(limit, q.x)),
      y: Math.max(-limit, Math.min(limit, q.y)),
      p: q.p,
    });
    for (let guard = 0; guard <= MAX_DEPTH; guard++) {
      let outOfBounds = false;
      for (const q of draw.points) {
        if (Math.abs(q.x) > limit || Math.abs(q.y) > limit) {
          outOfBounds = true;
          break;
        }
      }
      if (!outOfBounds) return;
      const cell = this.world.tree.get(draw.cellId);
      const parent = this.world.tree.parentOf(cell);
      if (!parent) {
        // Root overflow: clamp to the world edge.
        draw.points = draw.points.map(clampPt);
        return;
      }
      const m = cell.localToParent;
      draw.points = draw.points.map((q) => ({
        x: m.a * q.x + m.c * q.y + m.e,
        y: m.b * q.x + m.d * q.y + m.f,
        p: q.p,
      }));
      draw.cellId = parent.id;
      draw.style = { ...draw.style, size: draw.style.size * 0.5 };
      draw.brushScale *= 2;
    }
  }

  private activeLayerId(): string {
    const visible = this.world.layers.filter((l) => l.visible);
    return (visible[visible.length - 1] ?? this.world.layers[0])!.id;
  }

  /* ------------------------------- shapes -------------------------------- */

  private finishShape(): void {
    if (!this.shapeDraft) return;
    const { kind, start, current } = this.shapeDraft;
    this.shapeDraft = null;
    this.emit("overlay", undefined);
    const { w: cw, h: ch } = this.canvasSize;

    // Draft in a cell near the pointer (like strokes), then promote to a
    // parent if either corner exceeds the cell bounds.
    const { cellId } = this.strokeCellAt(
      { x: (start.x + current.x) / 2, y: (start.y + current.y) / 2 },
      Math.max(1, Math.abs(current.x - start.x), Math.abs(current.y - start.y)),
      "current",
    );
    const toCell = (pt: Vec2) =>
      this.camera.screenToCellPoint(cw / 2, ch / 2, cellId, pt);

    let a = toCell(start);
    let b = toCell(current);
    let cell = this.world.tree.get(cellId);
    let brushScale = this.pxPerUnit(cellId.length);
    const limit = WORLD_EXTENT;
    while (
      Math.abs(a.x) > limit || Math.abs(a.y) > limit ||
      Math.abs(b.x) > limit || Math.abs(b.y) > limit
    ) {
      const parent = this.world.tree.parentOf(cell);
      if (!parent) {
        // World edge: clamp both corners (like the edge of paper).
        const clampPt = (q: Vec2): Vec2 => ({
          x: Math.max(-limit, Math.min(limit, q.x)),
          y: Math.max(-limit, Math.min(limit, q.y)),
        });
        a = clampPt(a);
        b = clampPt(b);
        break;
      }
      const m = cell.localToParent;
      const rebase = (q: Vec2): Vec2 => ({
        x: m.a * q.x + m.c * q.y + m.e,
        y: m.b * q.x + m.d * q.y + m.f,
      });
      a = rebase(a);
      b = rebase(b);
      cell = parent;
      brushScale *= 2;
    }

    if (kind === "line") {
      const stroke = this.world.newStroke({
        layerId: this.activeLayerId(),
        cellId: cell.id,
        points: [
          { x: a.x, y: a.y, p: 1 },
          { x: b.x, y: b.y, p: 1 },
        ],
        style: toStrokeStyle({
          tool: "pen",
          color: this.style.color,
          size: this.style.size / brushScale,
          opacity: this.style.opacity,
        }),
      });
      this.world.addElement(stroke);
      this.history.push({ type: "add", elements: [stroke] });
      return;
    }

    const x = Math.min(a.x, b.x);
    const y = Math.min(a.y, b.y);
    const w = Math.abs(b.x - a.x);
    const h = Math.abs(b.y - a.y);
    if (Math.min(w, h) < 1e-4 / brushScale) return;
    const shape = this.world.newShape({
      layerId: this.activeLayerId(),
      cellId: cell.id,
      shape: kind === "ellipse" ? "ellipse" : "rect",
      x,
      y,
      w,
      h,
      color: this.style.color,
      size: this.style.size / brushScale,
      opacity: this.style.opacity,
      filled: false,
    });
    this.world.addElement(shape);
    this.history.push({ type: "add", elements: [shape] });
  }

  /* --------------------------- text / images ----------------------------- */

  /**
   * Ascend from `cellId` toward the root until `point` fits within the
   * drawable bounds (mirrors the promotion that strokes/shapes get in
   * extendStroke/fitDrawToCell/finishShape). Without this, a camera panned
   * far outside any cell extent would place text/images hundreds of
   * cell-units out of bounds — outside the tile cache, so the element is
   * stored (elementCount grows) but never rendered. The transform chain
   * preserves the world position, so the rebased point still lands exactly
   * under the pointer.
   */
  private fitPointToCell(cellId: string, point: Vec2): { cellId: string; point: Vec2 } {
    const limit = WORLD_EXTENT;
    let id = cellId;
    let p = point;
    for (let guard = 0; guard <= MAX_DEPTH; guard++) {
      if (Math.abs(p.x) <= limit && Math.abs(p.y) <= limit) return { cellId: id, point: p };
      const cell = this.world.tree.get(id);
      const parent = this.world.tree.parentOf(cell);
      if (!parent) {
        return {
          cellId: id,
          point: {
            x: clamp(p.x, -limit, limit),
            y: clamp(p.y, -limit, limit),
          },
        };
      }
      const m = cell.localToParent;
      p = { x: m.a * p.x + m.c * p.y + m.e, y: m.b * p.x + m.d * p.y + m.f };
      id = parent.id;
    }
    return { cellId: id, point: p };
  }

  private requestText(p: PointerState): void {
    const { cellId, point } = this.strokeCellAt({ x: p.x, y: p.y }, 24, "current");
    const fitted = this.fitPointToCell(cellId, point);
    this.emit("request-text", { cellId: fitted.cellId, x: fitted.point.x, y: fitted.point.y, targetScale: 24 });
  }

  private requestImage(p: PointerState): void {
    const { cellId, point } = this.strokeCellAt({ x: p.x, y: p.y }, 200, "current");
    const fitted = this.fitPointToCell(cellId, point);
    this.emit("request-image", { cellId: fitted.cellId, x: fitted.point.x, y: fitted.point.y, targetScale: 200 });
  }

  addText(
    cellId: string,
    x: number,
    y: number,
    text: string,
    fontSize: number,
    opts?: { color?: string; fontFamily?: string },
  ): void {
    const el = this.world.newText({
      layerId: this.activeLayerId(),
      cellId,
      x,
      y,
      text,
      fontSize,
      color: opts?.color ?? this.style.color,
      fontFamily: opts?.fontFamily ?? "system-ui, sans-serif",
    });
    this.world.addElement(el);
    this.history.push({ type: "add", elements: [el] });
  }

  addImage(cellId: string, x: number, y: number, w: number, h: number, src: string, opacity = 1): boolean {
    const created = this.world.newImage({
      layerId: this.activeLayerId(),
      cellId,
      x,
      y,
      w,
      h,
      src,
      opacity,
    });
    if ("error" in created) return false;
    this.world.addElement(created as ImageElement);
    this.history.push({ type: "add", elements: [created as ImageElement] });
    return true;
  }

  /* ----------------------------- eyedropper ------------------------------ */

  private eyedrop(p: PointerState): void {
    const { w: cw, h: ch } = this.canvasSize;
    const TOL_PX = 8;
    const visibleLayers = new Set(
      this.world.layers.filter((l) => l.visible).map((l) => l.id),
    );

    interface Hit {
      color: string;
      distPx: number;
      areaPx: number;
      index: number;
    }
    const hits: Hit[] = [];
    let order = 0;

    for (const [cellId, els] of Object.entries(this.world.allCells())) {
      if (els.length === 0) continue;
      const m = this.camera.cellToScreen(cw / 2, ch / 2, cellId);
      const pxPerUnit = linearScaleOf(m);
      if (!(pxPerUnit > 0) || !Number.isFinite(pxPerUnit)) continue;
      const tolCell = TOL_PX / pxPerUnit;
      const pt = this.camera.screenToCellPoint(cw / 2, ch / 2, cellId, p);

      for (const el of els) {
        const idx = order++;
        if (!visibleLayers.has(el.layerId)) continue;
        switch (el.kind) {
          case "stroke": {
            const d = distanceToStroke(el, pt);
            const half = strokeWidthAt(el, 1);
            if (d <= half + tolCell) {
              const b = strokeBounds(el);
              const areaPx =
                Math.max((b.maxX - b.minX) * (b.maxY - b.minY), 1e-12) *
                pxPerUnit *
                pxPerUnit;
              hits.push({
                color: el.style.color,
                distPx: Math.max(0, d - half) * pxPerUnit,
                areaPx,
                index: idx,
              });
            }
            break;
          }
          case "shape": {
            const b = elementBoundsInCell(el);
            const minX = Math.min(b.minX, b.maxX);
            const maxX = Math.max(b.minX, b.maxX);
            const minY = Math.min(b.minY, b.maxY);
            const maxY = Math.max(b.minY, b.maxY);
            if (el.shape === "rect") {
              const dx = Math.max(minX - pt.x, 0, pt.x - maxX);
              const dy = Math.max(minY - pt.y, 0, pt.y - maxY);
              const outside = Math.hypot(dx, dy);
              if (outside <= tolCell + el.size / 2) {
                const areaPx =
                  Math.max((maxX - minX) * (maxY - minY), 1e-12) *
                  pxPerUnit *
                  pxPerUnit;
                hits.push({ color: el.color, distPx: outside * pxPerUnit, areaPx, index: idx });
              }
            } else {
              const cx = el.x + el.w / 2;
              const cy = el.y + el.h / 2;
              const rx = Math.abs(el.w) / 2;
              const ry = Math.abs(el.h) / 2;
              if (rx > 0 && ry > 0) {
                const nx = (pt.x - cx) / rx;
                const ny = (pt.y - cy) / ry;
                const n = Math.hypot(nx, ny);
                if (n <= 1) {
                  const areaPx =
                    Math.max(Math.PI * rx * ry, 1e-12) * pxPerUnit * pxPerUnit;
                  hits.push({ color: el.color, distPx: 0, areaPx, index: idx });
                } else {
                  const outside = (n - 1) * Math.min(rx, ry);
                  if (outside <= tolCell + el.size / 2) {
                    const areaPx =
                      Math.max(Math.PI * rx * ry, 1e-12) * pxPerUnit * pxPerUnit;
                    hits.push({ color: el.color, distPx: outside * pxPerUnit, areaPx, index: idx });
                  }
                }
              }
            }
            break;
          }
          case "fill": {
            if (el.points.length >= 3) {
              if (pointInPolygon(pt, el.points)) {
                const b = elementBoundsInCell(el);
                const areaPx =
                  Math.max((b.maxX - b.minX) * (b.maxY - b.minY), 1e-12) *
                  pxPerUnit *
                  pxPerUnit;
                hits.push({ color: el.color, distPx: 0, areaPx, index: idx });
              } else {
                const d = distanceToPolygon(el.points, pt);
                if (d <= tolCell) {
                  const b = elementBoundsInCell(el);
                  const areaPx =
                    Math.max((b.maxX - b.minX) * (b.maxY - b.minY), 1e-12) *
                    pxPerUnit *
                    pxPerUnit;
                  hits.push({ color: el.color, distPx: d * pxPerUnit, areaPx, index: idx });
                }
              }
            }
            break;
          }
          case "text": {
            const b = elementBoundsInCell(el);
            const minX = Math.min(b.minX, b.maxX);
            const maxX = Math.max(b.minX, b.maxX);
            const minY = Math.min(b.minY, b.maxY);
            const maxY = Math.max(b.minY, b.maxY);
            const dx = Math.max(minX - pt.x, 0, pt.x - maxX);
            const dy = Math.max(minY - pt.y, 0, pt.y - maxY);
            const outside = Math.hypot(dx, dy);
            if (outside <= tolCell) {
              const areaPx =
                Math.max((maxX - minX) * (maxY - minY), 1e-12) *
                pxPerUnit *
                pxPerUnit;
              hits.push({ color: el.color, distPx: outside * pxPerUnit, areaPx, index: idx });
            }
            break;
          }
          case "image":
            break;
        }
      }
    }

    if (hits.length === 0) return;
    // Closest on screen wins; ties prefer the smallest (most specific)
    // element, then the topmost (later in z-order).
    hits.sort((a, b) => a.distPx - b.distPx || a.areaPx - b.areaPx || b.index - a.index);
    const best = hits[0]!;
    this.setColor(best.color);
    this.setTool(this.prevTool);
  }

  /* ------------------------------ bookmarks ------------------------------ */

  private placeBookmark(p: PointerState): void {
    const pt = this.camera.screenToFocus(this.canvasSize.w / 2, this.canvasSize.h / 2, p);
    this.emit("bookmark", {
      cellId: this.camera.focusCellId,
      x: pt.x,
      y: pt.y,
      scale: this.camera.scale,
    });
    this.setTool(this.prevTool);
  }

  /* --------------------------------- fill ---------------------------------- */

  /**
   * Paint-bucket fill at a screen point.
   *
   * Priority (first match wins):
   *  1. an existing fill polygon containing the point → recolor it
   *  2. a shape (rect/ellipse) containing the point → set filled + recolor it
   *  3. a stroke whose centerline encloses area containing the point
   *     (ends implicitly joined) → create a solid fill polygon from the
   *     loop in the stroke's own cell
   *  4. otherwise no-op (returns false).
   *
   * Only single-stroke enclosures are supported in v1: a region bounded by
   * several separate strokes is not detected. Returns true when the world
   * changed.
   */
  fillAt(screenPt: Vec2): boolean {
    const { w: cw, h: ch } = this.canvasSize;
    const color = this.style.color;
    const opacity = this.style.opacity;

    interface Hit {
      el: Element;
      area: number;
      index: number;
    }
    const shapeHits: Hit[] = [];
    const fillHits: Hit[] = [];
    const loopHits: Hit[] = [];
    let order = 0;

    for (const [cellId, els] of Object.entries(this.world.allCells())) {
      if (els.length === 0) continue;
      const ptInCell = this.camera.screenToCellPoint(cw / 2, ch / 2, cellId, screenPt);
      for (const el of els) {
        const idx = order++;
        if (el.kind === "fill") {
          if (el.points.length >= 3 && pointInPolygon(ptInCell, el.points)) {
            fillHits.push({ el, area: Math.abs(polygonArea(el.points)), index: idx });
          }
        } else if (el.kind === "shape") {
          if (shapeContainsPoint(el, ptInCell)) {
            const b = elementBoundsInCell(el);
            shapeHits.push({
              el,
              area: Math.abs((b.maxX - b.minX) * (b.maxY - b.minY)),
              index: idx,
            });
          }
        } else if (el.kind === "stroke") {
          // Hand-drawn loops rarely meet exactly — and endpoint smoothing
          // pulls sparse endpoints apart — so qualify by enclosed area
          // (ends implicitly joined) rather than endpoint gap. The area
          // must dwarf the stroke's own width so plain lines/scribbles
          // never qualify.
          if (el.points.length >= 3) {
            const area = Math.abs(polygonArea(el.points));
            const b = strokeBounds(el);
            const diag = Math.hypot(b.maxX - b.minX, b.maxY - b.minY);
            if (area > Math.max(diag * el.style.size, 1e-12) && pointInPolygon(ptInCell, el.points)) {
              loopHits.push({ el, area, index: idx });
            }
          }
        }
      }
    }

    // Most specific (smallest area), breaking ties by z-order (later = on top).
    const pick = (hits: Hit[]): Hit | null => {
      if (hits.length === 0) return null;
      hits.sort((a, b) => a.area - b.area || b.index - a.index);
      return hits[0]!;
    };

    const fillHit = pick(fillHits);
    if (fillHit) {
      const el = fillHit.el as FillElement;
      if (el.color === color && el.opacity === opacity) return false;
      const before = deepCopyElement(el);
      const after: FillElement = { ...deepCopyElement(el), color, opacity };
      this.world.replaceElement(after);
      this.history.push({ type: "replace", before: [before], after: [after] });
      this.emit("history", undefined);
      return true;
    }

    const shapeHit = pick(shapeHits);
    if (shapeHit) {
      const el = shapeHit.el as ShapeElement;
      if (el.filled && el.color === color && el.opacity === opacity) return false;
      const before = deepCopyElement(el);
      const after: ShapeElement = { ...deepCopyElement(el), filled: true, color, opacity };
      this.world.replaceElement(after);
      this.history.push({ type: "replace", before: [before], after: [after] });
      this.emit("history", undefined);
      return true;
    }

    const loopHit = pick(loopHits);
    if (loopHit) {
      const stroke = loopHit.el;
      if (stroke.kind !== "stroke") return false;
      const points = stroke.points.map((q) => ({ x: q.x, y: q.y }));
      if (points.length < 3) return false;
      const fill = this.world.newFill({
        layerId: this.activeLayerId(),
        cellId: stroke.cellId,
        points,
        color,
        opacity,
      });
      this.world.addElement(fill);
      this.history.push({ type: "add", elements: [fill] });
      this.emit("history", undefined);
      return true;
    }

    return false;
  }

  /* ------------------------------ selection ------------------------------ */

  private finishMarquee(): void {
    if (!this.marquee) return;
    const { start, current, lasso } = this.marquee;
    this.marquee = null;
    this.emit("overlay", undefined);
    const rect = {
      x: Math.min(start.x, current.x),
      y: Math.min(start.y, current.y),
      w: Math.abs(current.x - start.x),
      h: Math.abs(current.y - start.y),
    };
    const polygon = lasso && lasso.length >= 3 ? lasso : null;
    if (!polygon && rect.w < 4 && rect.h < 4) {
      this.clearSelection();
      return;
    }

    const items: SelectionItem[] = [];
    const seen = new Set<string>();
    const collect = (cellId: string, depthBudget: number) => {
      if (depthBudget < 0) return;
      for (const el of this.world.elementsInCell(cellId)) {
        if (seen.has(el.id)) continue;
        seen.add(el.id);
        if (this.elementInSelection(el, cellId, rect, polygon)) items.push({ el });
      }
      const cell = this.world.tree.get(cellId);
      for (const child of Object.values(cell.children)) {
        if (child) collect(child.id, depthBudget - 1);
      }
    };
    collect(this.camera.focusCellId, 4);

    if (items.length === 0) {
      this.clearSelection();
      return;
    }
    this.selection = { mode: "selected", items, box: this.selectionBox(items) };
    this.emit("selection", this.selection);
  }

  private elementInSelection(
    el: Element,
    cellId: string,
    rect: { x: number; y: number; w: number; h: number },
    polygon: Vec2[] | null,
  ): boolean {
    const center = this.elementScreenCenter(el, cellId);
    if (!center) return false;
    if (polygon) return pointInPolygon(center, polygon);
    return pointInBox(center, rect);
  }

  private elementScreenCenter(el: Element, cellId: string): Vec2 | null {
    const m = this.camera.cellToScreen(this.canvasSize.w / 2, this.canvasSize.h / 2, cellId);
    switch (el.kind) {
      case "stroke": {
        const b = strokeBounds(el);
        return apply(m, { x: (b.minX + b.maxX) / 2, y: (b.minY + b.maxY) / 2 });
      }
      case "shape":
        return apply(m, { x: el.x + el.w / 2, y: el.y + el.h / 2 });
      case "fill": {
        const b = elementBoundsInCell(el);
        return apply(m, { x: (b.minX + b.maxX) / 2, y: (b.minY + b.maxY) / 2 });
      }
      case "text": {
        const approxWidth = Math.max(el.fontSize, el.text.length * el.fontSize * 0.6);
        return apply(m, { x: el.x + approxWidth / 2, y: el.y + el.fontSize / 2 });
      }
      case "image":
        return apply(m, { x: el.x + el.w / 2, y: el.y + el.h / 2 });
    }
  }

  private selectionBox(items: SelectionItem[]): { x: number; y: number; w: number; h: number } {
    const { w, h } = this.canvasSize;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const it of items) {
      const m = this.camera.cellToScreen(w / 2, h / 2, it.el.cellId);
      const b = elementBoundsInCell(it.el);
      const c0 = apply(m, { x: b.minX, y: b.minY });
      const c1 = apply(m, { x: b.maxX, y: b.maxY });
      minX = Math.min(minX, c0.x, c1.x);
      minY = Math.min(minY, c0.y, c1.y);
      maxX = Math.max(maxX, c0.x, c1.x);
      maxY = Math.max(maxY, c0.y, c1.y);
    }
    if (!Number.isFinite(minX)) return { x: 0, y: 0, w: 0, h: 0 };
    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  }

  clearSelection(): void {
    this.selection = { mode: "none" };
    this.emit("selection", this.selection);
  }

  private beginSelectionDrag(p: PointerState, ctrl: boolean): void {
    if (this.selection.mode !== "selected") return;
    const box = this.selection.box;
    const handle = hitHandle(p, box);
    const startEls = new Map<string, Element>();
    for (const it of this.selection.items) startEls.set(it.el.id, deepCopyElement(it.el));
    this.selDrag = {
      mode: handle ? "scale" : "move",
      start: { x: p.x, y: p.y },
      startEls,
      duplicate: ctrl,
      handle,
      anchor: handle ? oppositeCorner(box, handle) : { x: box.x, y: box.y },
    };
  }

  private updateSelDrag(p: PointerState): void {
    if (!this.selDrag || this.selection.mode !== "selected") return;
    const { w, h } = this.canvasSize;
    const dx = p.x - this.selDrag.start.x;
    const dy = p.y - this.selDrag.start.y;

    for (const it of this.selection.items) {
      const original = this.selDrag.startEls.get(it.el.id);
      if (!original) continue;
      const fromScreen = this.camera.cellToScreen(w / 2, h / 2, it.el.cellId);
      const toScreen = this.camera.screenToCell(w / 2, h / 2, it.el.cellId);
      const cellK = linearScaleOf(fromScreen);

      if (this.selDrag.mode === "move") {
        it.el = translateElement(original, dx / cellK, dy / cellK);
      } else {
        const anchorScreen = this.selDrag.anchor;
        const origBox = this.selectionBox([{ el: original }]);
        const origHandle = oppositeCorner(origBox, this.selDrag.handle!);
        const d0 = Math.max(1e-3, dist(origHandle, anchorScreen));
        const factor = clamp(dist(p, anchorScreen) / d0, 0.01, 1000);
        const anchorCell = apply(toScreen, anchorScreen);
        it.el = scaleElement(original, anchorCell, factor);
      }
    }
    this.selection = { ...this.selection, box: this.selectionBox(this.selection.items) };
    this.emit("selection", this.selection);
  }

  private commitSelDrag(): void {
    if (!this.selDrag || this.selection.mode !== "selected") return;
    const before: Element[] = [];
    const after: Element[] = [];
    for (const it of this.selection.items) {
      const orig = this.selDrag.startEls.get(it.el.id);
      if (!orig) continue;
      if (this.selDrag.duplicate) {
        const copy = deepCopyElement(it.el);
        copy.id = newId(copy.kind);
        this.world.addElement(copy);
        after.push(copy);
      } else {
        before.push(orig);
        after.push(it.el);
        this.world.replaceElement(it.el);
      }
    }
    if (this.selDrag.duplicate) {
      this.history.push({ type: "add", elements: after });
      this.selection = {
        mode: "selected",
        items: after.map((el) => ({ el })),
        box: this.selectionBox(after.map((el) => ({ el }))),
      };
      this.emit("selection", this.selection);
    } else if (before.length) {
      this.history.push({ type: "replace", before, after });
    }
    this.selDrag = null;
  }

  deleteSelection(): void {
    if (this.selection.mode !== "selected") return;
    const els = this.selection.items.map((i) => i.el);
    for (const el of els) this.world.removeElement(el.cellId, el.layerId, el.id);
    this.history.push({ type: "remove", elements: els });
    this.clearSelection();
  }

  copySelection(): void {
    if (this.selection.mode !== "selected") return;
    const els = this.selection.items.map((i) => deepCopyElement(i.el));
    if (els.length === 0) return;
    const anchorCell = els[0]!.cellId;
    const box = this.selection.box;
    const center = this.camera.screenToCellPoint(
      this.canvasSize.w / 2,
      this.canvasSize.h / 2,
      anchorCell,
      { x: box.x + box.w / 2, y: box.y + box.h / 2 },
    );
    this.clipboard = { els, center };
  }

  paste(): void {
    if (!this.clipboard || this.clipboard.els.length === 0) return;
    const target = this.strokeCellAt(
      { x: this.canvasSize.w / 2, y: this.canvasSize.h / 2 },
      this.style.size,
      "current",
    );
    const els = this.clipboard.els.map((el) =>
      rebaseElement(el, this.world, target.cellId, target.point, this.clipboard!.center),
    );
    this.world.addElements(els);
    this.history.push({ type: "add", elements: els });
    this.selection = {
      mode: "selected",
      items: els.map((el) => ({ el })),
      box: this.selectionBox(els.map((el) => ({ el }))),
    };
    this.emit("selection", this.selection);
  }

  private selectAll(): void {
    const items: SelectionItem[] = [];
    const collect = (cellId: string, depth: number) => {
      if (depth > 3) return;
      for (const el of this.world.elementsInCell(cellId)) items.push({ el });
      const cell = this.world.tree.get(cellId);
      for (const child of Object.values(cell.children)) {
        if (child) collect(child.id, depth + 1);
      }
    };
    collect(this.camera.focusCellId, 0);
    if (items.length === 0) return;
    this.selection = { mode: "selected", items, box: this.selectionBox(items) };
    this.emit("selection", this.selection);
  }

  /* ------------------------------- overlay ------------------------------- */

  /** Smooth the eraser cursor toward the latest pointer position.
   *  Called every animation frame from the engine's render loop.
   *
   *  Returns `true` while the cursor is still moving, so the engine can skip
   *  repainting once it has settled (an idle eraser should not cost frames). */
  updateCursorSmoothing(): boolean {
    const target = this.lastPointerPos;
    if (!target) {
      const had = this.smoothedCursorPos !== null;
      this.smoothedCursorPos = null;
      return had;
    }
    if (!this.smoothedCursorPos) {
      this.smoothedCursorPos = { x: target.x, y: target.y };
      return true;
    }
    // Exponential smoothing: fast enough to feel responsive, smooth enough
    // to eliminate jitter from raw pointer events.
    const t = 0.35;
    const next = vLerp(this.smoothedCursorPos, target, t);
    // Sub-pixel deltas are invisible; treat them as settled so we stop painting.
    const moving = Math.abs(next.x - this.smoothedCursorPos.x) > 0.05 ||
      Math.abs(next.y - this.smoothedCursorPos.y) > 0.05;
    this.smoothedCursorPos = moving ? next : { x: target.x, y: target.y };
    return moving;
  }

  /** Draw tool UI (marquee, selection box/handles, shape drafts, eraser
   *  preview ring) in screen space. Called after the renderer each frame. */
  drawOverlay(ctx: CanvasRenderingContext2D, dpr: number): void {
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    if (this.marquee) {
      const { start, current, lasso } = this.marquee;
      ctx.strokeStyle = "#3b82f6";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([6, 4]);
      if (lasso) {
        ctx.beginPath();
        ctx.moveTo(lasso[0]!.x, lasso[0]!.y);
        for (let i = 1; i < lasso.length; i++) ctx.lineTo(lasso[i]!.x, lasso[i]!.y);
        ctx.lineTo(current.x, current.y);
        ctx.stroke();
      } else {
        ctx.strokeRect(
          Math.min(start.x, current.x),
          Math.min(start.y, current.y),
          Math.abs(current.x - start.x),
          Math.abs(current.y - start.y),
        );
      }
      ctx.setLineDash([]);
      return;
    }

    if (this.shapeDraft) {
      const { kind, start, current } = this.shapeDraft;
      ctx.fillStyle = this.style.color;
      ctx.globalAlpha = 0.08;
      if (kind === "rect") {
        ctx.fillRect(
          Math.min(start.x, current.x),
          Math.min(start.y, current.y),
          Math.abs(current.x - start.x),
          Math.abs(current.y - start.y),
        );
      } else if (kind === "ellipse") {
        ctx.beginPath();
        ctx.ellipse(
          (start.x + current.x) / 2,
          (start.y + current.y) / 2,
          Math.abs(current.x - start.x) / 2,
          Math.abs(current.y - start.y) / 2,
          0,
          0,
          Math.PI * 2,
        );
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      ctx.strokeStyle = this.style.color;
      ctx.lineWidth = Math.max(1.5, this.style.size);
      ctx.setLineDash([6, 4]);
      if (kind === "line") {
        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(current.x, current.y);
        ctx.stroke();
      } else if (kind === "rect") {
        ctx.strokeRect(
          Math.min(start.x, current.x),
          Math.min(start.y, current.y),
          Math.abs(current.x - start.x),
          Math.abs(current.y - start.y),
        );
      } else {
        ctx.beginPath();
        ctx.ellipse(
          (start.x + current.x) / 2,
          (start.y + current.y) / 2,
          Math.abs(current.x - start.x) / 2,
          Math.abs(current.y - start.y) / 2,
          0,
          0,
          Math.PI * 2,
        );
        ctx.stroke();
      }
      ctx.setLineDash([]);
      return;
    }

    if (this.selection.mode === "selected") {
      const box = this.selection.box;
      ctx.strokeStyle = "#3b82f6";
      ctx.lineWidth = 1.5;
      ctx.strokeRect(box.x, box.y, box.w, box.h);
      for (const handle of ["nw", "ne", "sw", "se"] as const) {
        const c = cornerOf(box, handle);
        ctx.fillStyle = "#ffffff";
        ctx.beginPath();
        ctx.arc(c.x, c.y, HANDLE_R, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = "#3b82f6";
        ctx.stroke();
      }
    }

    const pos = this.smoothedCursorPos || this.lastPointerPos || [...this.pointers.values()][0];
    if (pos && this.tool === "eraser") {
      ctx.strokeStyle = "rgba(60,60,60,0.8)";
      ctx.fillStyle = "rgba(220,220,220,0.15)";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, Math.max(this.style.size / 2, 8), 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }

    if (pos && this.tool === "fill") {
      ctx.strokeStyle = "rgba(60,60,60,0.8)";
      ctx.fillStyle = this.style.color;
      ctx.globalAlpha = Math.max(0.15, Math.min(0.5, this.style.opacity));
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(pos.x, pos.y, 9, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.stroke();
      // Bucket spout tick.
      ctx.beginPath();
      ctx.moveTo(pos.x + 6, pos.y + 6);
      ctx.lineTo(pos.x + 11, pos.y + 11);
      ctx.stroke();
    }
  }
}

/* ------------------------------ helpers ---------------------------------- */

function newId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function pointInPolygon(p: Vec2, poly: Vec2[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const pi = poly[i]!;
    const pj = poly[j]!;
    if (pi.y > p.y !== pj.y > p.y && p.x < ((pj.x - pi.x) * (p.y - pi.y)) / (pj.y - pi.y) + pi.x) {
      inside = !inside;
    }
  }
  return inside;
}

function pointInBox(p: Vec2, box: { x: number; y: number; w: number; h: number }): boolean {
  return p.x >= box.x && p.x <= box.x + box.w && p.y >= box.y && p.y <= box.y + box.h;
}

/** Signed polygon area (shoelace); absolute value ≈ interior size. */
function polygonArea(poly: Vec2[]): number {
  let a = 0;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    a += (poly[j]!.x + poly[i]!.x) * (poly[j]!.y - poly[i]!.y);
  }
  return a / 2;
}

/** True when a stroke's centerline forms a closed loop (for bucket fill). */
export function isClosedStroke(stroke: Stroke): boolean {
  const pts = stroke.points;
  if (pts.length < 3) return false;
  const first = pts[0]!;
  const last = pts[pts.length - 1]!;
  const gap = Math.hypot(last.x - first.x, last.y - first.y);
  const b = strokeBounds(stroke);
  const diag = Math.hypot(b.maxX - b.minX, b.maxY - b.minY);
  const width = Math.max(stroke.style.size, 1e-6);
  // Generous closure tolerance: hand-drawn loops rarely meet exactly.
  const tol = Math.max(width * 2, diag * 0.08, 1e-4);
  return gap <= tol;
}

/** True when a cell-local point lies inside a rect/ellipse shape. */
function shapeContainsPoint(el: ShapeElement, p: Vec2): boolean {
  if (el.shape === "rect") {
    const minX = Math.min(el.x, el.x + el.w);
    const maxX = Math.max(el.x, el.x + el.w);
    const minY = Math.min(el.y, el.y + el.h);
    const maxY = Math.max(el.y, el.y + el.h);
    return p.x >= minX && p.x <= maxX && p.y >= minY && p.y <= maxY;
  }
  const cx = el.x + el.w / 2;
  const cy = el.y + el.h / 2;
  const rx = Math.abs(el.w) / 2;
  const ry = Math.abs(el.h) / 2;
  if (rx <= 0 || ry <= 0) return false;
  const dx = (p.x - cx) / rx;
  const dy = (p.y - cy) / ry;
  return dx * dx + dy * dy <= 1;
}

/** Minimum distance from a point to a polygon's edges. */
function distanceToPolygon(poly: Vec2[], p: Vec2): number {
  if (poly.length === 0) return Infinity;
  if (poly.length === 1) return Math.hypot(p.x - poly[0]!.x, p.y - poly[0]!.y);
  let best = Infinity;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    best = Math.min(best, distToSegment(p, poly[j]!, poly[i]!));
  }
  return best;
}

function distToSegment(p: Vec2, a: Vec2, b: Vec2): number {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const len2 = abx * abx + aby * aby;
  if (len2 < 1e-12) return Math.hypot(p.x - a.x, p.y - a.y);
  let t = ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + abx * t), p.y - (a.y + aby * t));
}

function deepCopyElement<T extends Element>(el: T): T {
  return JSON.parse(JSON.stringify(el)) as T;
}

/** Axis-aligned bounds of an element in its cell's local units. */
export function elementBoundsInCell(el: Element): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
} {
  switch (el.kind) {
    case "stroke":
      return strokeBounds(el);
    case "shape":
      return { minX: el.x, minY: el.y, maxX: el.x + el.w, maxY: el.y + el.h };
    case "fill": {
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (const q of el.points) {
        minX = Math.min(minX, q.x);
        minY = Math.min(minY, q.y);
        maxX = Math.max(maxX, q.x);
        maxY = Math.max(maxY, q.y);
      }
      if (!Number.isFinite(minX)) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
      return { minX, minY, maxX, maxY };
    }
    case "text": {
      const approxWidth = Math.max(el.fontSize, el.text.length * el.fontSize * 0.6);
      return { minX: el.x, minY: el.y, maxX: el.x + approxWidth, maxY: el.y + el.fontSize };
    }
    case "image":
      return { minX: el.x, minY: el.y, maxX: el.x + el.w, maxY: el.y + el.h };
  }
}

function translateElement<T extends Element>(el: T, dx: number, dy: number): T {
  const copy = deepCopyElement(el);
  switch (copy.kind) {
    case "stroke":
      for (const pt of copy.points) {
        pt.x += dx;
        pt.y += dy;
      }
      break;
    case "fill":
      for (const pt of copy.points) {
        pt.x += dx;
        pt.y += dy;
      }
      break;
    case "shape":
    case "text":
    case "image":
      copy.x += dx;
      copy.y += dy;
      break;
  }
  return copy;
}

function scaleElement<T extends Element>(el: T, anchor: Vec2, factor: number): T {
  const copy = deepCopyElement(el);
  const sx = (v: number) => anchor.x + (v - anchor.x) * factor;
  const sy = (v: number) => anchor.y + (v - anchor.y) * factor;
  switch (copy.kind) {
    case "stroke":
      for (const pt of copy.points) {
        pt.x = sx(pt.x);
        pt.y = sy(pt.y);
      }
      copy.style = { ...copy.style, size: copy.style.size * factor };
      break;
    case "shape":
      copy.x = sx(copy.x);
      copy.y = sy(copy.y);
      copy.w *= factor;
      copy.h *= factor;
      copy.size *= factor;
      break;
    case "fill":
      for (const pt of copy.points) {
        pt.x = sx(pt.x);
        pt.y = sy(pt.y);
      }
      break;
    case "text":
      copy.x = sx(copy.x);
      copy.y = sy(copy.y);
      copy.fontSize *= factor;
      break;
    case "image":
      copy.x = sx(copy.x);
      copy.y = sy(copy.y);
      copy.w *= factor;
      copy.h *= factor;
      break;
  }
  return copy;
}

function linearScaleOf(m: Mat2D): number {
  return Math.sqrt(Math.abs(m.a * m.d - m.b * m.c));
}

function cornerOf(box: { x: number; y: number; w: number; h: number }, handle: "nw" | "ne" | "sw" | "se"): Vec2 {
  switch (handle) {
    case "nw": return { x: box.x, y: box.y };
    case "ne": return { x: box.x + box.w, y: box.y };
    case "sw": return { x: box.x, y: box.y + box.h };
    case "se": return { x: box.x + box.w, y: box.y + box.h };
  }
}

function oppositeCorner(box: { x: number; y: number; w: number; h: number }, handle: "nw" | "ne" | "sw" | "se"): Vec2 {
  switch (handle) {
    case "nw": return { x: box.x + box.w, y: box.y + box.h };
    case "ne": return { x: box.x, y: box.y + box.h };
    case "sw": return { x: box.x + box.w, y: box.y };
    case "se": return { x: box.x, y: box.y };
  }
}

function hitHandle(p: Vec2, box: { x: number; y: number; w: number; h: number }): "nw" | "ne" | "sw" | "se" | null {
  for (const handle of ["nw", "ne", "sw", "se"] as const) {
    if (dist(p, cornerOf(box, handle)) <= HANDLE_R + 2) return handle;
  }
  return null;
}

/** Rebase an element into a different cell, preserving physical size. */
function rebaseElement(
  el: Element,
  world: World,
  targetCellId: string,
  targetPoint: Vec2,
  sourceCenter: Vec2,
): Element {
  const copy = deepCopyElement(el);
  const k = Math.pow(2, targetCellId.length - el.cellId.length);
  const m = world.tree.transform(el.cellId, targetCellId);
  const tf = (p: Vec2): Vec2 => apply(m, p);
  const centerTarget = tf(sourceCenter);
  const delta = { x: targetPoint.x - centerTarget.x, y: targetPoint.y - centerTarget.y };
  const mapPt = (p: Vec2): Vec2 => {
    const q = tf(p);
    return { x: q.x + delta.x, y: q.y + delta.y };
  };

  copy.cellId = targetCellId;
  switch (copy.kind) {
    case "stroke":
      copy.points = copy.points.map((q) => {
        const r = mapPt(q);
        return { x: r.x, y: r.y, p: q.p };
      });
      copy.style = { ...copy.style, size: copy.style.size * k };
      break;
    case "fill":
      copy.points = copy.points.map((q) => mapPt(q));
      break;
    case "shape":
    case "text":
    case "image": {
      const p0 = mapPt({ x: copy.x, y: copy.y });
      copy.x = p0.x;
      copy.y = p0.y;
      if (copy.kind === "shape") {
        copy.w *= k;
        copy.h *= k;
        copy.size *= k;
      } else if (copy.kind === "text") {
        copy.fontSize *= k;
      } else {
        copy.w *= k;
        copy.h *= k;
      }
      break;
    }
  }
  return copy;
}
